import {
  createScriptElement,
  removeElements,
} from '@src/quince-site-common/src/trackers/providers/tracking-provider-dom-utils'
import { executeSafely } from '@src/quince-site-common/src/trackers/providers/tracking-provider-utils'
import { wait } from '@src/utils/wait'

type Task = () => void

export interface InitializeScriptOptions {
  key: string
  domain: string
  path: string | null | (() => string | null)
  minDelay?: number
  maxDelay?: number
  async?: boolean // default to true
  defer?: boolean // default to true
  init?: () => void | Promise<void>
  onLoad?: (scriptElement: HTMLScriptElement | null) => void
  onError?: (msg: string, error: unknown) => void
  injectAt?: 'head' | 'beforeFirstScript'
  attributes?: Record<string, string>
}

function executeOnDocumentReadyStateComplete(task: Task): Task {
  return () => {
    if (document.readyState === 'complete') {
      task()
    } else {
      const listener = () => {
        if (document.readyState === 'complete') {
          task()
          document.removeEventListener('readystatechange', listener)
        }
      }
      document.addEventListener('readystatechange', listener)
    }
  }
}

function createScriptTag({
  key,
  domain,
  path: pathOrGetPath,
  async = true,
  defer = true,
  attributes,
  onLoad,
  onError,
}: InitializeScriptOptions): HTMLScriptElement | null {
  const path = typeof pathOrGetPath === 'function' ? pathOrGetPath() : pathOrGetPath
  if (!(typeof path === 'string' && path.length > 0)) {
    return null
  }
  if (attributes?.id) {
    removeElements(`script#${attributes.id}`)
  }
  const script = createScriptElement(
    { async, defer },
    {
      src: domain + path,
      ...(attributes ?? {}),
    },
  )

  if (typeof onLoad === 'function') {
    script.onload = executeSafely(
      executeOnDocumentReadyStateComplete(() => {
        onLoad(script)
      }),
      `TrackingProviderLoader:onScriptLoad(${key})`,
      onError,
    )
  }
  return script
}

function insertIntoHead(script: HTMLScriptElement): void {
  document.head.appendChild(script)
}

function insertBeforeFirstScript(script: HTMLScriptElement): void {
  const firstScriptTag = document.body.getElementsByTagName('script')[0]
  if (firstScriptTag?.parentNode) {
    firstScriptTag.parentNode.insertBefore(script, firstScriptTag)
  } else {
    document.body.appendChild(script)
  }
}

async function createScriptAndInsertIntoDocument(config: InitializeScriptOptions) {
  const script = createScriptTag(config)
  if (script) {
    if (config.injectAt === 'head') {
      insertIntoHead(script)
    } else if (config.injectAt === 'beforeFirstScript') {
      insertBeforeFirstScript(script)
    } else {
      document.body.appendChild(script)
    }
  } else if (typeof config.onLoad === 'function') {
    await executeSafely(
      executeOnDocumentReadyStateComplete(() => {
        if (config.onLoad) config.onLoad(null)
      }),
      `TrackingProviderLoader:onLoadWithoutScript(${config.key})`,
      config.onError,
    )()
  }
}

function pageLoadPromise() {
  return new Promise(resolve => {
    if (document.readyState === 'complete') {
      resolve(null)
    } else {
      const listener = () => {
        if (document.readyState === 'complete') {
          document.removeEventListener('readystatechange', listener)
          resolve(null)
        }
      }
      document.addEventListener('readystatechange', listener)
    }
  })
}

async function addScript(config: InitializeScriptOptions) {
  if (config.minDelay) {
    await wait(config.minDelay).then(async () => {
      await createScriptAndInsertIntoDocument(config)
    })
  } else if (config.maxDelay) {
    await Promise.race([pageLoadPromise(), wait(config.maxDelay)]).then(async () => {
      await createScriptAndInsertIntoDocument(config)
    })
  } else {
    await createScriptAndInsertIntoDocument(config)
  }
}

export function initializeScript(options: InitializeScriptOptions) {
  return executeSafely(
    async () => {
      if (typeof options.init === 'function') {
        await options.init()
      }
      if (options.path ?? options.onLoad) {
        await addScript(options)
      }
    },
    `TrackingProviderLoader:loadTrackingProvider(${options.key})`,
    options.onError,
  )()
}
